import React from 'react';
import InfiniteScroll from 'react-infinite-scroll-component';
import { format } from 'date-fns';
import {
  chakra,
  Checkbox,
  CheckboxProps,
  Flex,
  Image,
  Skeleton,
  Tooltip,
} from '@chakra-ui/react';
import { ListItemText, TableList } from 'app/components/ChakraTableList';
import { PlaylistsListItem } from 'types/playlists.types';
import { dateFormats } from 'app/constants';
import { useInfiniteLoader } from 'app/utils/hooks';
import { getPlaylistsNextLink } from 'app/store/Playlists/selectors';
import { fetchPlaylistsEffect } from 'app/store/Playlists/effects';
import { ReactComponent as PlaylistIcon } from 'resources/images/playlist.svg';
import { PLAYLISTS_SELECT_SCROLL_ID } from 'app/components/Playlist/PlaylistsSelectList/constants';
import { ReactComponent as PlaylistsIcon } from 'resources/images/playlists-huge.svg';
import { ChakraEmptyList } from 'app/components/Core';

interface PlaylistsSelectListProps {
  playlists: PlaylistsListItem[];
  loading: boolean;
  selectedPlaylists: PlaylistsListItem[];
  maxPlaylistAudios: number;
  onPlaylistSelect: (playlist: PlaylistsListItem) => void;
}

export const PlaylistsSelectList = ({
  playlists,
  loading,
  selectedPlaylists,
  maxPlaylistAudios,
  onPlaylistSelect,
}: PlaylistsSelectListProps) => {
  const [, infiniteScrollProps, loadingMore] = useInfiniteLoader({
    listId: PLAYLISTS_SELECT_SCROLL_ID,
    dataLength: playlists.length,
    nextLinkSelector: getPlaylistsNextLink,
    fetchNext: (link) => fetchPlaylistsEffect({ next: link }),
  });

  if (!playlists.length && !loading) {
    return (
      <ChakraEmptyList
        icon={<PlaylistsIcon role="img" />}
        title="You don't have any playlists yet"
        primaryText="Build your own playlist for listening and sharing to A&R or future collaborators"
        primaryTextSx={{ maxWidth: '630px' }}
      />
    );
  }

  return (
    <InfiniteScroll {...infiniteScrollProps}>
      <TableList
        memoizeColumnFormatter
        loadingOptions={{
          loading,
          loadingMore,
          repeat: 1,
          loader: {
            accessory: <Skeleton width="40px" height="40px" />,
            title: <Skeleton width="240px" height="24px" />,
            modified: <Skeleton width="86px" height="24px" />,
            created: <Skeleton width="86px" height="24px" />,
            files: <Skeleton width="24px" height="24px" />,
          },
        }}
        columns={[
          {
            key: 'accessory',
            width: 48,
            name: '',
            centered: true,
            accessory: true,
          },
          { key: 'title', name: '' },
          { key: 'checkbox', name: '', contextMenu: true },
        ]}
        onRowClick={(row: { rowData: PlaylistsListItem }) => {
          if (row.rowData.audioCount < maxPlaylistAudios) {
            onPlaylistSelect(row.rowData);
          }
        }}
        rows={playlists.map((playlist, index) => {
          const selectedGuids = (selectedPlaylists || []).map((el) => el.guid);

          const playlistImage = playlist.images.find(
            (image) => image.name === 'playlist-tiny'
          );

          return {
            clickable: playlist.audioCount < maxPlaylistAudios,
            disabled: playlist.audioCount === maxPlaylistAudios,
            rowData: playlist,
            accessory: (
              <Image
                src={playlistImage?.href}
                alt="playlist cover"
                fallback={<PlaylistIcon width="40px" height="40px" />}
              />
            ),
            title: (
              <ListItemText
                primary={playlist.title}
                title={playlist.title}
                data-testid="playlist-title"
                secondary={format(
                  new Date(playlist.created.iso),
                  dateFormats.LIST_FORMAT
                )}
                secondaryTextProps={{
                  'data-testid': 'playlist-created',
                  fontSize: 'xs',
                  color: 'textSubdued',
                }}
              />
            ),
            checkbox: (
              <Flex
                position="relative"
                justifyContent="center"
                alignItems="center"
                height="full"
                onClick={(e) => {
                  e.preventDefault();
                }}
              >
                <Tooltip
                  isDisabled={playlist.audioCount < maxPlaylistAudios}
                  closeDelay={500}
                  label="Maximum number of audio files reached for this playlist"
                >
                  <chakra.span display="flex" data-testid="checkbox">
                    <Checkbox
                      isChecked={selectedGuids.includes(playlist.guid)}
                      isDisabled={playlist.audioCount === maxPlaylistAudios}
                      inputProps={
                        {
                          'data-testid': `check-playlist-${index}`,
                        } as CheckboxProps['inputProps']
                      }
                    />
                  </chakra.span>
                </Tooltip>
              </Flex>
            ),
          };
        })}
      />
    </InfiniteScroll>
  );
};
